import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Loader2 } from 'lucide-react';

// Module-level "auth has been resolved at least once" flag. Once we've seen
// a successful auth check, subsequent route changes never block on the
// `loading` flag again — we just render children and let the useEffect
// redirect to /login if it later turns out the user signed out. This kills
// the "Loading…" flash between protected routes that the user reported as
// the second skeleton-style flicker (after the page-level skeletons were
// already silenced).
let _authEverResolved = false;

export const AuthCallback = () => {
  const { processOAuthSession } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Use useRef to prevent processing twice in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        try {
          await processOAuthSession(sessionId);
          // Clear the hash and redirect to dashboard
          window.history.replaceState(null, '', '/dashboard');
          navigate('/dashboard', { replace: true });
        } catch (error) {
          console.error('OAuth processing failed:', error);
          navigate('/login', { replace: true });
        }
      } else {
        navigate('/login', { replace: true });
      }
    };

    processAuth();
  }, [location.hash, processOAuthSession, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
        <p className="text-muted-foreground">Completing sign in...</p>
      </div>
    </div>
  );
};

export const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!loading) {
      _authEverResolved = true;
      if (!isAuthenticated) {
        navigate('/login', { state: { from: location }, replace: true });
      }
    }
  }, [isAuthenticated, loading, navigate, location]);

  // Only show the full-screen loader on the very first auth check. After that,
  // any transient `loading=true` (e.g. token refresh) keeps the previous UI.
  if (loading && !_authEverResolved) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated && !loading) {
    return null;
  }

  return children;
};

// Admin-only route - redirects non-admin users to Location page
export const AdminRoute = ({ children }) => {
  const { isAuthenticated, isAdmin, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!loading) {
      _authEverResolved = true;
      if (!isAuthenticated) {
        navigate('/login', { state: { from: location }, replace: true });
      } else if (!isAdmin) {
        // Non-admin users trying to access admin routes - redirect to Location
        navigate('/locations', { replace: true });
      }
    }
  }, [isAuthenticated, isAdmin, loading, navigate, location]);

  // Same SWR-style gate as ProtectedRoute — only block on first auth check.
  if (loading && !_authEverResolved) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if ((!isAuthenticated || !isAdmin) && !loading) {
    return null;
  }

  return children;
};
